// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Grid, Paper, Chip, useMediaQuery, Card, alpha } from '@mui/material';
import { ArrowForward, Security, AccountBalance, Payments, MobileFriendly, Diamond, TrendingUp, SupportAgent, Speed, Stars, WorkspacePremium, Verified, FlashOn } from '@mui/icons-material';
import { motion } from "framer-motion";

// Professional DiamondBank color palette - Dark Gray/Black with subtle purple accent
const colors = {
  primary: '#1a1a1a',
  primaryLight: '#2d2d2d',
  primaryDark: '#0d0d0d',
  accent: '#6b21a5',
  accentLight: '#8b5cc9',
  accentDark: '#4c1d6e',
  secondary: '#e5e7eb',
  white: '#ffffff',
  black: '#000000',
  gray: '#6b7280',
  grayLight: '#9ca3af',
  grayDark: '#374151',
  lightBg: '#f8f9fa',
  textDark: '#1a1a1a',
  textLight: '#ffffff',
  gold: '#d4af37'
};

// Import all images from public folder
const bankImage = '/images/DB - Retail Shopfront-mockup (June 24, 2018).png';
const bankInterior = '/images/pic.png';
const beautImage = '/images/cd.png'; // New hero background image
const j4 = '/images/db2 top.png';
const welcomeImage = '/images/herosection.png';

// Business Solutions Image - Updated with your specified image
const businessInteriorImage = '/images/diamondbankinterio.png';

// Hero card images
const diamondCardGold = '/images/diamondcardgoldaccount.png';
const diamondAccountCard = '/images/diamondaccountcard.png';

// Premium cards images - Using your specified images
const diamondAccountCardImg = '/images/diamondaccountcard.png';
const silverAccountCardImg = '/images/diamondbanksilveraccountcard.png';
const goldAccountCardImg = '/images/diamondcardgoldaccount.png';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const slideInFromLeft = {
  hidden: { x: -50, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
};

const slideInFromRight = {
  hidden: { x: 50, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
};

const Home = () => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const services = [
    {
      icon: <Security sx={{ fontSize: 50, color: colors.accent }} />,
      title: 'Secure Banking',
      content: 'Advanced security measures with multi-factor authentication and real-time fraud monitoring'
    },
    {
      icon: <AccountBalance sx={{ fontSize: 50, color: colors.accent }} />,
      title: 'Business Loans',
      content: 'Flexible financing options with competitive rates for businesses of all sizes'
    },
    {
      icon: <Payments sx={{ fontSize: 50, color: colors.accent }} />,
      title: 'Easy Payments',
      content: 'Seamless payment solutions including mobile money, card, and online transfers'
    },
    {
      icon: <MobileFriendly sx={{ fontSize: 50, color: colors.accent }} />,
      title: 'Mobile Banking',
      content: 'Bank anytime, anywhere with our award-winning mobile app available on iOS and Android'
    }
  ];

  // All three premium cards with updated images
  const premiumCards = [
    {
      icon: diamondAccountCardImg,
      title: 'DIAMOND ACCOUNT',
      content: 'Premium banking with exclusive rewards',
      gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
      textColor: colors.white,
      features: ['5% cashback', 'Personal banker', 'Travel insurance', 'Global lounge access'],
      cardColor: '#1a1a1a'
    },
    {
      icon: goldAccountCardImg,
      title: 'GOLD ACCOUNT',
      content: 'Ultimate banking experience with premium benefits',
      gradient: `linear-gradient(135deg, #d4af37 0%, #f5d76e 50%, #d4af37 100%)`,
      textColor: colors.textDark,
      features: ['8% cashback', '24/7 concierge', 'Global lounge access', 'Premium rewards'],
      cardColor: '#d4af37'
    },
    {
      icon: silverAccountCardImg,
      title: 'SILVER ACCOUNT',
      content: 'Essential banking with premium benefits',
      gradient: `linear-gradient(135deg, #C0C0C0 0%, #E8E8E8 50%, #C0C0C0 100%)`,
      textColor: colors.textDark,
      features: ['2% cashback', 'Free ATM withdrawals', 'Mobile banking', 'No annual fee'],
      cardColor: '#C0C0C0'
    }
  ];

  const newsItems = [
    {
      title: 'Digital Banking Transformation',
      content: 'Experience our redesigned digital platform with enhanced security features and seamless navigation across all devices.',
      tag: 'Digital Banking',
      image: bankImage
    },
    {
      title: 'Premium Cards Launch',
      content: 'Introducing our exclusive Platinum and Diamond cards with unprecedented benefits, rewards, and global recognition.',
      tag: 'Cards',
      image: diamondCardGold
    },
    {
      title: 'Business Solutions',
      content: 'New business banking packages designed to help entrepreneurs grow, scale, and succeed in today\'s competitive market.',
      tag: 'Business',
      image: j4
    }
  ];

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
        {/* ============================================ */}
        {/* PREMIUM HERO SECTION - Black & Purple Theme */}
        {/* Using Diamond Card Gold and Diamond Account Card images */}
        {/* ============================================ */}
        <Box sx={{
          height: isMobile ? 'auto' : '90vh',
          minHeight: isMobile ? '600px' : '700px',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          py: { xs: 6, md: 0 }
        }}>
          {/* Background Image */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            backgroundImage: `url(${beautImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }} />

          {/* Premium Black & Purple Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${colors.primary}dd 0%, ${colors.primaryDark}aa 60%, ${colors.accent}88 100%)`,
              zIndex: 1
            }}
          />

          {/* Subtle Purple Glow Effect */}
          <Box sx={{
            position: 'absolute',
            top: '20%',
            right: '-10%',
            width: '50%',
            height: '60%',
            background: `radial-gradient(circle, ${alpha(colors.accent, 0.15)} 0%, transparent 70%)`,
            borderRadius: '50%',
            filter: 'blur(80px)',
            zIndex: 1,
            pointerEvents: 'none'
          }} />

          {/* Hero Content */}
          <Container maxWidth="xl" sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            py: 4
          }}>
            <Grid container spacing={4} alignItems="center">
              {/* Left Text Content - Premium styling */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  {/* Premium Badge - Purple themed */}
                  <Box sx={{ mb: 3 }}>
                    <Chip
                      icon={<Stars sx={{ fontSize: 16, color: colors.accentLight }} />}
                      label="PREMIUM BANKING"
                      sx={{
                        backgroundColor: alpha(colors.accent, 0.2),
                        color: colors.accentLight,
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(colors.accent, 0.4)}`,
                        py: 0.5,
                        px: 1
                      }}
                    />
                  </Box>

                  {/* Main Headline - WHITE FONT COLOR */}
                  <Typography
                    variant="h1"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      fontSize: isMobile ? '3rem' : '5rem',
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                      mb: 2,
                      color: colors.white,
                      textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                    }}
                  >
                    The{' '}
                    <span style={{ 
                      background: `linear-gradient(135deg, ${colors.white}, ${colors.accentLight}, ${colors.gold})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      Safe
                    </span>{' '}
                    Bank
                  </Typography>

                  {/* Subtitle - GOLD COLOR for "Gold Backed • One to One on the Dollar" */}
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontWeight: 600,
                      color: colors.white,
                      fontSize: isMobile ? '1.2rem' : '1.5rem',
                      lineHeight: 1.4,
                      maxWidth: '550px',
                      letterSpacing: '0.5px'
                    }}
                  >
                    <span style={{ color: colors.gold, fontWeight: 700 }}>Gold Backed</span>
                    <span style={{ margin: '0 8px', color: alpha(colors.gold, 0.5) }}>•</span>
                    <span style={{ color: colors.gold, fontWeight: 700 }}>One to One on the Dollar</span>
                  </Typography>

                  {/* Trust Indicators - Premium pills with gold accent - WHITE TEXT */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 5, flexWrap: 'wrap' }}>
                    {[
                      { icon: <Verified sx={{ fontSize: 16 }} />, text: 'Gold Backed' },
                      { icon: <WorkspacePremium sx={{ fontSize: 16 }} />, text: 'One to One on the Dollar' },
                      { icon: <Security sx={{ fontSize: 16 }} />, text: 'Fully Reserved' }
                    ].map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                          backgroundColor: alpha(colors.gold, 0.15),
                          backdropFilter: 'blur(10px)',
                          borderRadius: '40px',
                          px: 1.8,
                          py: 0.8,
                          border: `1px solid ${alpha(colors.gold, 0.3)}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: alpha(colors.gold, 0.25),
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <Box sx={{ color: colors.gold }}>{item.icon}</Box>
                        <Typography variant="body2" sx={{ color: colors.white, fontWeight: 600, fontSize: '0.8rem' }}>
                          {item.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* CTA Buttons - Premium styling */}
                  <Box sx={{
                    display: 'flex',
                    gap: 3,
                    flexWrap: 'wrap'
                  }}>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant="contained"
                      size="large"
                      href="/personal"
                      sx={{
                        px: 5,
                        py: 1.5,
                        fontSize: '1rem',
                        borderRadius: '50px',
                        background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold}dd)`,
                        color: colors.primary,
                        fontWeight: 700,
                        boxShadow: `0 8px 25px ${alpha(colors.gold, 0.4)}`,
                        '&:hover': {
                          background: colors.gold,
                          transform: 'translateY(-3px)',
                          boxShadow: `0 12px 30px ${alpha(colors.gold, 0.6)}`
                        }
                      }}
                    >
                      Open Account
                    </Button>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant="outlined"
                      size="large"
                      href="/about"
                      sx={{
                        px: 5,
                        py: 1.5,
                        fontSize: '1rem',
                        borderRadius: '50px',
                        borderWidth: '2px',
                        borderColor: alpha(colors.white, 0.8),
                        color: colors.white,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: alpha(colors.white, 0.1),
                          borderColor: colors.white,
                          transform: 'translateY(-3px)'
                        }
                      }}
                    >
                      Discover More
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              {/* Right Content - Card Stack with your specified images */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                  style={{
                    position: 'relative',
                    height: isMobile ? '380px' : '500px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {/* Glassmorphic Card Container - Premium glass effect */}
                  <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {/* Backdrop Glass Effect Layer with purple tint */}
                    <Box sx={{
                      position: 'absolute',
                      width: '85%',
                      height: '85%',
                      background: `linear-gradient(135deg, ${alpha(colors.white, 0.1)}, ${alpha(colors.accent, 0.05)})`,
                      backdropFilter: 'blur(15px)',
                      borderRadius: '48px',
                      boxShadow: '0 25px 50px rgba(0,0,0,0.2), inset 0 1px 1px rgba(255,255,255,0.2)',
                      border: `1px solid ${alpha(colors.accent, 0.3)}`,
                      zIndex: 0
                    }} />
                    
                    {/* Diamond Card Gold - Top card with premium animation */}
                    <motion.div
                      animate={{ y: [0, -12, 0] }}
                      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                      style={{ position: 'absolute', left: '5%', top: '8%', zIndex: 1 }}
                    >
                      <Box
                        component="img"
                        src={diamondCardGold}
                        alt="Diamond Card Gold"
                        sx={{
                          width: isMobile ? '200px' : '280px',
                          height: 'auto',
                          borderRadius: '28px',
                          boxShadow: '0 30px 60px rgba(0,0,0,0.5), 0 0 0 2px rgba(255,215,0,0.3)',
                          transform: 'rotate(-12deg)',
                          transition: 'transform 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1)',
                          '&:hover': {
                            transform: 'rotate(-8deg) scale(1.03)'
                          }
                        }}
                      />
                    </motion.div>

                    {/* Diamond Account Card - Bottom card with premium animation */}
                    <motion.div
                      animate={{ y: [0, 8, 0] }}
                      transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                      style={{ position: 'absolute', left: '12%', top: '22%', zIndex: 2 }}
                    >
                      <Box
                        component="img"
                        src={diamondAccountCard}
                        alt="Diamond Account Card"
                        sx={{
                          width: isMobile ? '240px' : '360px',
                          height: 'auto',
                          borderRadius: '32px',
                          boxShadow: '0 35px 70px rgba(0,0,0,0.6), 0 0 0 2px rgba(255,255,255,0.25)',
                          transform: 'rotate(-3deg)',
                          transition: 'transform 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1)',
                          '&:hover': {
                            transform: 'rotate(0deg) scale(1.02)'
                          }
                        }}
                      />
                    </motion.div>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>

          {/* Scroll Indicator - Gold themed */}
          <Box sx={{
            position: 'absolute',
            bottom: 25,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            opacity: 0.8,
            transition: 'opacity 0.3s',
            '&:hover': { opacity: 1 }
          }}
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}>
            <Typography variant="caption" sx={{ color: colors.gold, fontWeight: 600, letterSpacing: 2 }}>SCROLL</Typography>
            <Box sx={{
              width: 2,
              height: 35,
              backgroundColor: colors.gold,
              borderRadius: 2,
              animation: 'scrollPulse 2s ease-in-out infinite'
            }} />
          </Box>
        </Box>

        {/* Services Section */}
        <Box sx={{ py: 12, backgroundColor: colors.white }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 10 }}>
              <motion.div variants={itemVariants} viewport={{ once: true }}>
                <Chip
                  label="OUR SERVICES"
                  sx={{
                    backgroundColor: alpha(colors.accent, 0.1),
                    color: colors.accent,
                    fontWeight: 700,
                    letterSpacing: 1,
                    mb: 3,
                    fontSize: '0.85rem',
                    px: 2,
                    height: 30,
                    borderRadius: '8px'
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: colors.primary,
                    mb: 3,
                    fontSize: isMobile ? '2.2rem' : '3rem',
                    lineHeight: 1.2
                  }}
                >
                  Comprehensive Banking Solutions
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.gray,
                    maxWidth: '720px',
                    margin: '0 auto',
                    fontSize: isMobile ? '1rem' : '1.15rem',
                    lineHeight: 1.7
                  }}
                >
                  Empowering individuals and businesses with innovative financial services designed for growth and success
                </Typography>
              </motion.div>
            </Box>

            <Grid container spacing={4} justifyContent="center" alignItems="stretch">
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.6, type: "spring", stiffness: 100 }}
                    whileHover={{ y: -6 }}
                    style={{ height: "100%" }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 4,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        textAlign: 'center',
                        borderRadius: '22px',
                        background: 'white',
                        boxShadow: '0 8px 28px rgba(0,0,0,0.05)',
                        border: `1px solid ${alpha(colors.gray, 0.1)}`,
                        transition: 'all 0.35s ease',
                        '&:hover': {
                          boxShadow: '0 16px 40px rgba(0,0,0,0.1)',
                          transform: 'translateY(-8px)',
                          border: `1px solid ${colors.accent}`
                        }
                      }}
                    >
                      <Box
                        sx={{
                          mb: 3,
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          boxShadow: `0 6px 15px ${alpha(colors.primary, 0.3)}`
                        }}
                      >
                        {service.icon}
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary, mb: 2 }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray, flexGrow: 1, fontSize: '0.9rem', lineHeight: 1.65 }}>
                        {service.content}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Welcome Section - STATIC IMAGE */}
        <Box sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(180deg, white 0%, ${colors.lightBg} 100%)`,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: alpha(colors.accent, 0.03),
            filter: 'blur(120px)',
            zIndex: 0
          }} />
          <Container sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={8} alignItems="center" direction={isMobile ? "column-reverse" : "row"}>
              <Grid item xs={12} md={6}>
                <motion.div variants={slideInFromLeft} viewport={{ once: true }}>
                  <Typography
                    variant="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                      color: colors.primary,
                      position: 'relative',
                      display: 'inline-block'
                    }}
                  >
                    Welcome To <span style={{ color: colors.accent }}>DiamondBank</span>
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3, color: colors.gray, opacity: 0.85 }}>
                    As Zimbabwe's premier financial institution, we combine cutting-edge digital technology with
                    deep industry expertise to create innovative banking solutions that empower our customers.
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4, color: colors.gray, opacity: 0.85 }}>
                    Our customer-centric approach focuses on understanding your unique financial needs —
                    providing tailored solutions that help you grow, innovate, and secure your financial future.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant="contained"
                      href="/about"
                      endIcon={<ArrowForward />}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        borderRadius: '30px',
                        fontWeight: 600,
                        background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                        boxShadow: `0 6px 20px ${alpha(colors.accent, 0.3)}`,
                        '&:hover': { boxShadow: `0 10px 25px ${alpha(colors.accent, 0.4)}` }
                      }}
                    >
                      Our Story
                    </Button>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant="outlined"
                      href="/contact"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        borderRadius: '30px',
                        fontWeight: 600,
                        borderWidth: '2px',
                        borderColor: colors.accent,
                        color: colors.accent,
                        '&:hover': { backgroundColor: alpha(colors.accent, 0.05), transform: 'translateY(-2px)' }
                      }}
                    >
                      Contact Us
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  variants={slideInFromRight}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                  style={{
                    position: 'relative',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: `0 20px 60px ${alpha(colors.primary, 0.2)}`,
                    height: '450px'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${welcomeImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative'
                    }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 4,
                    background: `linear-gradient(to top, rgba(0,0,0,0.7), transparent)`,
                    color: 'white'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Digital Banking</Typography>
                    <Typography variant="body1">Our Future Headquater</Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* ============================================ */}
        {/* PREMIUM BANKING CARDS SECTION - UPDATED WITH YOUR IMAGES */}
        {/* ============================================ */}
        <Box sx={{ py: 12, backgroundColor: colors.white, position: 'relative', overflow: 'hidden' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <motion.div variants={itemVariants} viewport={{ once: true }}>
                <Chip
                  label="EXCLUSIVE OFFERINGS"
                  sx={{
                    backgroundColor: alpha(colors.accent, 0.1),
                    color: colors.accent,
                    fontWeight: 700,
                    letterSpacing: 1,
                    mb: 3,
                    fontSize: '0.9rem',
                    height: '32px'
                  }}
                />
                <Typography variant="h2" sx={{ fontWeight: 700, color: colors.primary, mb: 2, fontSize: isMobile ? '2.5rem' : '3rem' }}>
                  Premium Banking Cards
                </Typography>
                <Typography variant="h6" sx={{ color: colors.gray, maxWidth: '700px', margin: '0 auto', fontSize: isMobile ? '1rem' : '1.2rem' }}>
                  Elevate your banking experience with our exclusive range of premium cards,
                  each designed to offer unique benefits tailored to your lifestyle
                </Typography>
              </motion.div>
            </Box>

            <Grid container spacing={4} justifyContent="center">
              {premiumCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5, type: "spring", stiffness: 100 }}
                    whileHover={{ y: -10 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '50px',
                        background: 'white',
                        boxShadow: `0 10px 30px ${alpha(colors.primary, 0.08)}`,
                        border: `1px solid ${alpha(colors.gray, 0.1)}`,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 20px 40px ${alpha(colors.primary, 0.15)}`,
                          transform: 'translateY(-5px)'
                        }
                      }}
                    >
                      {/* Card Header with Gradient */}
                      <Box sx={{
                        height: '60px',
                        background: card.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderTopLeftRadius: '50px',
                        borderTopRightRadius: '50px',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        {/* Shimmer effect for gold card */}
                        {card.title === 'GOLD ACCOUNT' && (
                          <Box sx={{
                            position: 'absolute',
                            top: '-50%',
                            left: '-50%',
                            width: '200%',
                            height: '200%',
                            background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)',
                            animation: 'shimmer 3s infinite'
                          }} />
                        )}
                        <Typography variant="h6" sx={{ 
                          color: card.textColor, 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          textShadow: card.title === 'DIAMOND ACCOUNT' ? '0 2px 10px rgba(0,0,0,0.5)' : 'none'
                        }}>
                          {card.title}
                        </Typography>
                      </Box>
                      
                      {/* Card Image */}
                      <Box sx={{
                        height: '260px',
                        background: `linear-gradient(135deg, ${colors.lightBg} 0%, ${colors.white} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                        position: 'relative'
                      }}>
                        {/* Glow effect behind card */}
                        <Box sx={{
                          position: 'absolute',
                          width: '80%',
                          height: '80%',
                          borderRadius: '50%',
                          background: `radial-gradient(circle, ${alpha(card.cardColor, 0.1)} 0%, transparent 70%)`,
                          filter: 'blur(40px)'
                        }} />
                        <Box
                          component="img"
                          src={card.icon}
                          alt={card.title}
                          sx={{
                            width: '80%',
                            height: 'auto',
                            maxWidth: '300px',
                            filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.2))',
                            transition: 'transform 0.3s ease',
                            '&:hover': { transform: 'scale(1.05) rotate(2deg)' }
                          }}
                        />
                      </Box>
                      
                      {/* Card Content */}
                      <Box sx={{ p: 3, flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ color: colors.gray, textAlign: 'center', mb: 2, lineHeight: 1.5 }}>
                          {card.content}
                        </Typography>
                        
                        {/* Feature Chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mb: 2 }}>
                          {card.features.map((feature, idx) => (
                            <Chip
                              key={idx}
                              label={feature}
                              size="small"
                              sx={{
                                backgroundColor: alpha(card.cardColor, 0.1),
                                color: colors.primary,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                borderRadius: '20px',
                                border: `1px solid ${alpha(card.cardColor, 0.2)}`
                              }}
                            />
                          ))}
                        </Box>
                        
                        <Button
                          component={motion.button}
                          whileHover={{ scale: 1.03 }}
                          variant="outlined"
                          fullWidth
                          sx={{
                            fontWeight: 600,
                            borderRadius: '50px',
                            py: 1.5,
                            textTransform: 'none',
                            borderColor: card.cardColor,
                            color: card.cardColor,
                            '&:hover': { 
                              backgroundColor: card.cardColor, 
                              color: 'white', 
                              borderColor: card.cardColor,
                              boxShadow: `0 4px 15px ${alpha(card.cardColor, 0.3)}`
                            }
                          }}
                        >
                          Apply Now
                        </Button>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: '50px',
                  background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  boxShadow: `0 4px 20px ${alpha(colors.accent, 0.3)}`,
                  '&:hover': { background: colors.accentLight, transform: 'translateY(-2px)' }
                }}
              >
                View All Card Options
              </Button>
            </Box>
          </Container>
        </Box>

        {/* ============================================ */}
        {/* BUSINESS SOLUTIONS SECTION - UPDATED WITH YOUR IMAGE */}
        {/* ============================================ */}
        <Box sx={{ py: 8, backgroundColor: colors.lightBg }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  borderRadius: '50px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  border: `1px solid ${alpha(colors.accent, 0.2)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                    transform: 'translateY(-5px)'
                  }
                }}
              >
                {/* Image Section - Using your specified image */}
                <Box
                  sx={{
                    width: { xs: '100%', md: '40%' },
                    height: { xs: '250px', md: 'auto' },
                    minHeight: { md: '350px' },
                    backgroundImage: `url(${businessInteriorImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, ${alpha(colors.accent, 0.1)}, ${alpha(colors.primary, 0.05)})`,
                    }
                  }}
                />
                
                {/* Content Section */}
                <Box
                  sx={{
                    p: { xs: 4, md: 6 },
                    flex: 1,
                    background: `linear-gradient(135deg, ${colors.white} 0%, ${alpha(colors.accent, 0.03)} 100%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: { xs: 'center', md: 'flex-start' },
                    textAlign: { xs: 'center', md: 'left' }
                  }}
                >
                  {/* Premium Badge */}
                  <Chip
                    label="BUSINESS SOLUTIONS"
                    sx={{
                      backgroundColor: alpha(colors.accent, 0.1),
                      color: colors.accent,
                      fontWeight: 700,
                      letterSpacing: 1,
                      mb: 3,
                      fontSize: '0.75rem',
                      px: 2,
                      height: 30,
                      borderRadius: '8px'
                    }}
                  />
                  
                  <Typography 
                    variant="h3" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700, 
                      color: colors.primary, 
                      mb: 2,
                      fontSize: isMobile ? '2rem' : '2.8rem',
                      lineHeight: 1.2
                    }}
                  >
                    Business Solutions
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: colors.grayDark, 
                      mb: 4, 
                      lineHeight: 1.6,
                      maxWidth: '560px',
                      fontSize: isMobile ? '1rem' : '1.2rem',
                      fontWeight: 400
                    }}
                  >
                    New business banking packages designed to help entrepreneurs grow, scale, 
                    and succeed in today's competitive market.
                  </Typography>
                  
                  {/* Feature Bullets */}
                  <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: { xs: 'center', md: 'flex-start' } }}>
                    {[
                      'Flexible financing options',
                      'Business advisory services',
                      'Digital payment solutions',
                      'International trade support'
                    ].map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          backgroundColor: colors.accent,
                          flexShrink: 0
                        }} />
                        <Typography variant="body1" sx={{ color: colors.gray, fontWeight: 500 }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{
                      px: 5,
                      py: 1.5,
                      borderRadius: '50px',
                      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                      fontWeight: 600,
                      fontSize: '1rem',
                      boxShadow: `0 4px 20px ${alpha(colors.accent, 0.3)}`,
                      '&:hover': { 
                        background: colors.accentLight, 
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${alpha(colors.accent, 0.4)}`
                      }
                    }}
                  >
                    Explore Business Banking
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          </Container>
        </Box>

        {/* Stats Section */}
        <Box sx={{
          py: 10,
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          color: 'white',
          position: 'relative'
        }}>
          <Container>
            <Grid container spacing={4} justifyContent="center" textAlign="center">
              {[
                { value: '24/7', label: 'Premium Support', icon: <SupportAgent sx={{ fontSize: 40, mb: 1 }} /> },
                { value: '100%', label: 'Secure', icon: <Security sx={{ fontSize: 40, mb: 1 }} /> },
                { value: '99.99%', label: 'Uptime', icon: <Speed sx={{ fontSize: 40, mb: 1 }} /> },
                { value: '50K+', label: 'Happy Clients', icon: <TrendingUp sx={{ fontSize: 40, mb: 1 }} /> }
              ].map((item, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", stiffness: 100, delay: index * 0.1 }}
                  >
                    {item.icon}
                    <Typography variant="h2" sx={{ fontWeight: 700, color: colors.accent, mb: 1, mt: 1 }}>
                      {item.value}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.9 }}>
                      {item.label}
                    </Typography>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{
          py: 12,
          background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold}dd 100%)`,
          color: colors.primary,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Container maxWidth="md">
            <motion.div variants={fadeIn} viewport={{ once: true }}>
              <Diamond sx={{ fontSize: 60, color: colors.primary, mb: 3, animation: 'spin 10s linear infinite' }} />
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: colors.primary, mb: 2, fontSize: isMobile ? '2.5rem' : '3.5rem' }}>
                Start Your Financial Journey Today
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 400, color: colors.primary, fontSize: isMobile ? '1.1rem' : '1.3rem' }}>
                Join our satisfied customers who trust DiamondBank with their financial future
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained"
                  size="large"
                  href="/personal"
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    backgroundColor: colors.primary,
                    color: colors.white,
                    fontWeight: 700,
                    '&:hover': { backgroundColor: colors.primaryLight }
                  }}
                >
                  Open an Account
                </Button>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: colors.primary,
                    color: colors.primary,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: alpha(colors.primary, 0.1) }
                  }}
                >
                  Talk to Advisor
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>

        {/* Latest News Section */}
        <Box sx={{ py: 10, backgroundColor: colors.lightBg }}>
          <Container>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <motion.div variants={itemVariants} viewport={{ once: true }}>
                <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1, fontSize: '1.2rem' }}>
                  STAY INFORMED
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: colors.primary,
                    position: 'relative',
                    display: 'inline-block',
                    mb: 4,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -15,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '100px',
                      height: '4px',
                      background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Latest News
                </Typography>
              </motion.div>
            </Box>
            <Grid container spacing={4} justifyContent="center">
              {newsItems.map((news, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.15 }}
                    whileHover={{ y: -5 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '50px',
                        background: 'white',
                        boxShadow: `0 10px 30px ${alpha(colors.primary, 0.05)}`,
                        border: `1px solid ${alpha(colors.gray, 0.1)}`,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 15px 35px ${alpha(colors.primary, 0.1)}`,
                          borderColor: alpha(colors.accent, 0.2)
                        }
                      }}
                    >
                      <Box component="img" src={news.image} alt={news.title} sx={{ 
                        width: '100%', 
                        height: 250, 
                        objectFit: 'cover',
                        borderTopLeftRadius: '50px',
                        borderTopRightRadius: '50px'
                      }} />
                      <Box sx={{ p: 4, flexGrow: 1 }}>
                        <Chip label={news.tag} sx={{ mb: 2, backgroundColor: alpha(colors.accent, 0.1), color: colors.accent, fontWeight: 600, borderRadius: '50px' }} />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: colors.primary, mb: 2 }}>
                          {news.title}
                        </Typography>
                        <Typography paragraph sx={{ color: colors.gray, mb: 3, fontSize: '0.95rem', lineHeight: 1.6 }}>
                          {news.content}
                        </Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Button
                            component={motion.button}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            variant="text"
                            endIcon={<ArrowForward />}
                            sx={{ fontWeight: 600, color: colors.accent, borderRadius: '50px', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
                          >
                            Read more
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* CSS animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes scrollPulse {
            0%, 100% { height: 30px; opacity: 0.5; }
            50% { height: 45px; opacity: 1; }
          }

          @keyframes shimmer {
            0% { transform: translateX(-100%) rotate(25deg); }
            100% { transform: translateX(100%) rotate(25deg); }
          }
        `}
      </style>
    </motion.div>
  );
};

export default Home;